import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { formatDate, useModalControl, usePopoverMenu } from '@frontend/design-system';
import { useScheduleSettingsSmallMode } from '../../../../../../hooks/settings/use-schedule-settings-small-mode';
import { useScheduleRequestNotificationsAsRead } from './use-schedule-request-notifications-as-read';

export const useScheduleRequestItemHeaderController = ({
  requestVersion,
  locationId,
  scheduleRequestId,
  appointmentTime,
  isWidgetListItem,
  isPreferenceBased = false,
  deleteRequestModalProps,
  dismissRequestModalProps,
  rejectRequestModalProps,
  onCompleteRequest,
  onMessage,
  onPhoneCall,
}: {
  requestVersion: string;
  scheduleRequestId: string;
  locationId: string;
  appointmentTime: string;
  isWidgetListItem?: boolean;
  isPreferenceBased?: boolean;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  dismissRequestModalProps?: ReturnType<typeof useModalControl>;
  rejectRequestModalProps?: ReturnType<typeof useModalControl>;
  onCompleteRequest?: () => void;
  onMessage: (updateStatus: boolean) => void;
  onPhoneCall?: () => void;
}) => {
  const { t } = useTranslation('schedule-request');
  const { markNotificationsAsRead } = useScheduleRequestNotificationsAsRead();
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { getLocationName } = useAppScopeStore();
  const locationName = getLocationName(locationId);
  const shouldUseV3 = requestVersion !== 'v2' && isScheduleV3FlagEnabled;
  const appointmentTimeLabel =
    shouldUseV3 && !isWidgetListItem && !isSmallMode
      ? t('Requested on {{requestTime}}', { requestTime: formatDate(appointmentTime, 'MMM D YYYY, h:mm A') })
      : formatDate(appointmentTime, 'MMM D YYYY, h:mm A');

  const {
    getMenuProps,
    getTriggerProps,
    close: closePopOverMenu,
  } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const markAndClose = () => {
    markNotificationsAsRead(scheduleRequestId);
    closePopOverMenu();
  };

  const handleReviewRequestMenuItemClick = (onReviewRequest: () => void) => {
    onReviewRequest();
    markAndClose();
  };

  const handleMessageMenuItemClick = async () => {
    onMessage(isPreferenceBased);
    markAndClose();
  };

  const handleCallMenuItemClick = async () => {
    onPhoneCall?.();
    markAndClose();
  };

  const handleDoneMenuItemClick = () => {
    onCompleteRequest?.();
    markAndClose();
  };

  const handleDeleteMenuItemClick = () => {
    deleteRequestModalProps.openModal();
    markAndClose();
  };

  const handleDismissMenuItemClick = () => {
    dismissRequestModalProps?.openModal();
    markAndClose();
  };

  const handleRejectRequestMenuItemClick = () => {
    rejectRequestModalProps?.openModal();
    markAndClose();
  };

  return {
    shouldUseV3,
    locationName,
    appointmentTimeLabel,
    getMenuProps,
    getTriggerProps,
    handleReviewRequestMenuItemClick,
    handleRejectRequestMenuItemClick,
    handleCallMenuItemClick,
    handleMessageMenuItemClick,
    handleDeleteMenuItemClick,
    handleDismissMenuItemClick,
    handleDoneMenuItemClick,
  };
};
